import { z } from 'zod';

// Import status types from enums to avoid duplicate exports
import {
  IncidentSeverity,
  IncidentSeveritySchema,
  ServiceHealthStatus,
  ServiceHealthStatusSchema,
  TestRunStatus,
  TestRunStatusSchema,
} from '../enums/sla.enums';

// Re-export for backwards compatibility
export {
  IncidentSeverity,
  IncidentSeveritySchema,
  ServiceHealthStatus,
  ServiceHealthStatusSchema,
  TestRunStatus,
  TestRunStatusSchema,
};

// ============================================
// SERVICE HEALTH TYPES
// ============================================

export const ServiceHealthSchema = z.object({
  serviceId: z.number(),
  serviceName: z.string(),
  displayName: z.string(),
  status: ServiceHealthStatusSchema,
  latencyMs: z.number().nullable(),
  lastCheckAt: z.string().datetime().nullable(),
  errorMessage: z.string().nullable(),
  uptime: z.number().min(0).max(100).nullable(), // percentage
  protocol: z.enum(['trpc', 'grpc', 'rest', 'n/a']),
  port: z.number().nullable(),
});
export type ServiceHealth = z.infer<typeof ServiceHealthSchema>;

export const ServiceHealthOverviewSchema = z.object({
  services: z.array(ServiceHealthSchema),
  summary: z.object({
    total: z.number(),
    healthy: z.number(),
    degraded: z.number(),
    unhealthy: z.number(),
    unknown: z.number(),
  }),
  lastUpdated: z.string().datetime(),
});
export type ServiceHealthOverview = z.infer<typeof ServiceHealthOverviewSchema>;

// ============================================
// TEST RUN TYPES
// ============================================

export const TestRunSchema = z.object({
  id: z.string(),
  scenarioId: z.string(),
  scenarioName: z.string(),
  status: TestRunStatusSchema,
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  durationMs: z.number().nullable(),
  failureReason: z.string().nullable(),
  nodeResults: z.array(
    z.object({
      nodeId: z.string(),
      status: z.string(),
      durationMs: z.number(),
      error: z.string().nullable(),
    }),
  ),
});
export type TestRun = z.infer<typeof TestRunSchema>;

export const TestRunsResponseSchema = z.object({
  data: z.array(TestRunSchema),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
  }),
  /** Error message if data fetch failed (optional for backward compatibility) */
  error: z.string().optional(),
});
export type TestRunsResponse = z.infer<typeof TestRunsResponseSchema>;

// ============================================
// INCIDENT TYPES
// ============================================

export const IncidentSchema = z.object({
  id: z.string(),
  testRunId: z.string(),
  severity: IncidentSeveritySchema,
  service: z.string().nullable(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()),
  alertsSent: z.record(z.string(), z.boolean()).nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Incident = z.infer<typeof IncidentSchema>;

export const IncidentsResponseSchema = z.object({
  data: z.array(IncidentSchema),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
  }),
  /** Error message if data fetch failed (optional for backward compatibility) */
  error: z.string().optional(),
});
export type IncidentsResponse = z.infer<typeof IncidentsResponseSchema>;

// ============================================
// METRICS TYPES
// ============================================

export const LatencyMetricsSchema = z.object({
  timestamp: z.string().datetime(),
  p50: z.number(),
  p95: z.number(),
  p99: z.number(),
  avg: z.number(),
});
export type LatencyMetrics = z.infer<typeof LatencyMetricsSchema>;

export const SuccessRateMetricsSchema = z.object({
  timestamp: z.string().datetime(),
  passed: z.number(),
  failed: z.number(),
  total: z.number(),
  rate: z.number().min(0).max(100),
});
export type SuccessRateMetrics = z.infer<typeof SuccessRateMetricsSchema>;

export const MetricsTimeSeriesSchema = z.object({
  latency: z.array(LatencyMetricsSchema),
  successRate: z.array(SuccessRateMetricsSchema),
  timeRange: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    granularity: z.enum(['1m', '5m', '15m', '1h', '6h', '1d']),
  }),
  /** Error message if data fetch failed (optional for backward compatibility) */
  error: z.string().optional(),
});
export type MetricsTimeSeries = z.infer<typeof MetricsTimeSeriesSchema>;

// ============================================
// NETWORK/API LOGS TYPES
// ============================================

export const ApiLogEntrySchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  service: z.string(),
  method: z.string(),
  path: z.string(),
  statusCode: z.number(),
  durationMs: z.number(),
  requestSize: z.number().nullable(),
  responseSize: z.number().nullable(),
  error: z.string().nullable(),
  sandboxId: z.string().nullable(),
});
export type ApiLogEntry = z.infer<typeof ApiLogEntrySchema>;

export const ApiLogsResponseSchema = z.object({
  data: z.array(ApiLogEntrySchema),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
  }),
});
export type ApiLogsResponse = z.infer<typeof ApiLogsResponseSchema>;

// ============================================
// SANDBOX METRICS TYPES
// ============================================

export const SandboxMetricsSchema = z.object({
  totalActive: z.number(),
  byStatus: z.record(z.string(), z.number()),
  byType: z.record(z.string(), z.number()),
  createdToday: z.number(),
  expiringSoon: z.number(), // within 24 hours
  totalStorage: z.number(), // MB
  avgApiCallsPerDay: z.number(),
});
export type SandboxMetrics = z.infer<typeof SandboxMetricsSchema>;

// ============================================
// DASHBOARD SUMMARY TYPES
// ============================================

export const DashboardSummarySchema = z.object({
  serviceHealth: ServiceHealthOverviewSchema,
  testMetrics: z.object({
    last24h: z.object({
      totalRuns: z.number(),
      passedRuns: z.number(),
      failedRuns: z.number(),
      successRate: z.number(),
      avgLatencyMs: z.number(),
    }),
    trend: z.enum(['up', 'down', 'stable']),
  }),
  incidents: z.object({
    open: z.number(),
    critical: z.number(),
    warning: z.number(),
    resolvedToday: z.number(),
  }),
  sandboxMetrics: SandboxMetricsSchema,
  lastUpdated: z.string().datetime(),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;

// ============================================
// ALERT CONFIGURATION TYPES
// ============================================

export const AlertConfigurationSchema = z.object({
  id: z.string(),
  slackWebhookUrl: z.string().nullable(),
  emailAddresses: z.string().nullable(),
  pagerDutyKey: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  criticalLevel: z.number(),
  warningLevel: z.number(),
  enableSlack: z.boolean(),
  enableEmail: z.boolean(),
  enablePagerDuty: z.boolean(),
  enableWebhook: z.boolean(),
});
export type AlertConfiguration = z.infer<typeof AlertConfigurationSchema>;

// ============================================
// PER-SERVICE METRICS TYPES (Prometheus aggregated)
// ============================================

/**
 * Per-service metrics aggregated from Prometheus
 * These metrics are consistent across all services
 */
export const ServiceMetricsSchema = z.object({
  serviceName: z.string(),
  // Request metrics (from gRPC/HTTP calls to this service)
  requests: z.object({
    total: z.number(), // Total requests in time window
    rate: z.number(), // Requests per second
    successCount: z.number(),
    errorCount: z.number(),
    errorRate: z.number().min(0).max(100), // Percentage
  }),
  // Latency metrics (from histogram)
  latency: z.object({
    p50: z.number().nullable(), // Median latency in ms
    p95: z.number().nullable(), // 95th percentile in ms
    p99: z.number().nullable(), // 99th percentile in ms
    avg: z.number().nullable(), // Average latency in ms
  }),
  // Error breakdown
  errors: z.object({
    total: z.number(),
    byType: z.record(z.string(), z.number()), // e.g., { "timeout": 5, "connection_refused": 2 }
  }),
  // Time window for these metrics
  timeWindow: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    durationSeconds: z.number(),
  }),
  // Timestamp when metrics were collected
  collectedAt: z.string().datetime(),
});
export type ServiceMetrics = z.infer<typeof ServiceMetricsSchema>;

export const ServiceMetricsResponseSchema = z.object({
  metrics: ServiceMetricsSchema.nullable(),
  available: z.boolean(), // Whether metrics are available for this service
  message: z.string().optional(), // Explanation if not available
});
export type ServiceMetricsResponse = z.infer<typeof ServiceMetricsResponseSchema>;

// ============================================
// REPORT TYPES
// ============================================

export const MonitoringReportSchema = z.object({
  id: z.string(),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  totalRuns: z.number(),
  passedRuns: z.number(),
  failedRuns: z.number(),
  successRate: z.number(),
  avgLatencyMs: z.number(),
  p95LatencyMs: z.number(),
  p99LatencyMs: z.number(),
  incidentSummary: z.object({
    CRITICAL: z.number(),
    WARNING: z.number(),
    INFO: z.number(),
  }),
  generatedAt: z.string().datetime(),
});
export type MonitoringReport = z.infer<typeof MonitoringReportSchema>;
